import React, { useEffect, useRef, useState } from 'react';
import { HiCheckCircle, HiXCircle, HiExclamationCircle, HiBell, HiInformationCircle } from 'react-icons/hi';
import { classificarMensagem, isContingencia } from '../../utils/classificarMensagem';
import { useMonitoramento } from '../../context/MonitoramentoContext';
import { tipoFaltante } from '../../utils/faltantes';

const nivelKey = { 'Moderado': 'moderado', 'Normal': 'normal', 'Grave': 'grave' };
const TITULO_PADRAO = 'Compsoft - Monitoramento';

const BUMP_CLASS = { danger: ' cs-kpi-bump-danger', warning: ' cs-kpi-bump-warning' };

// Anima o valor exibido (contagem progressiva) e pisca o card quando o
// número sobe para um status de atenção (grave/moderado).
function KpiCard({ item, loading, isClickable, isActive, onClick }) {
  const [displayValue, setDisplayValue] = useState(item.value);
  const [bump, setBump] = useState('');
  const prevValueRef = useRef(item.value);
  const rafRef = useRef(null);
  const bumpTimeoutRef = useRef(null);

  useEffect(() => {
    const from = prevValueRef.current;
    const to = item.value;
    if (from === to) return undefined;

    if (to > from && BUMP_CLASS[item.status]) {
      setBump(BUMP_CLASS[item.status]);
      clearTimeout(bumpTimeoutRef.current);
      bumpTimeoutRef.current = setTimeout(() => setBump(''), 1600);
    }

    const duration = 500;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(from + (to - from) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevValueRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(bumpTimeoutRef.current);
    };
  }, [item.value, item.status]);

  return (
    <div
      className={`cs-kpi-card cs-kpi-${item.status}${isClickable ? ' cs-kpi-clickable' : ''}${isActive ? ' cs-kpi-active' : ''}${bump}`}
      onClick={onClick}
    >
      <div className="cs-kpi-icon">{item.icon}</div>
      <div className="cs-kpi-body">
        <span className="cs-kpi-value">{loading ? '—' : displayValue}</span>
        <span className="cs-kpi-label">{item.label}</span>
        {item.sublabel && (
          <span className="cs-kpi-sublabel" style={{ color: 'inherit', opacity: 0.7 }}>
            {item.sublabel}
          </span>
        )}
      </div>
    </div>
  );
}

const DashboardKPIs = ({ filtroNivel, onFilterNivel }) => {
  const { pdvData, awsData, lastUpdate } = useMonitoramento();

  const loading = pdvData === null;

  const counts = { grave: 0, moderado: 0, normal: 0, online: 0, offline: 0 };
  if (!loading) {
    pdvData.filter(r => r.cnpj !== '11111111111111' && !isContingencia(r.mensagem) && !tipoFaltante(r)).forEach(r => {
      const { nivel } = classificarMensagem(r.mensagem);
      const key = nivelKey[nivel];
      if (key) counts[key]++;
    });
    counts.online  = awsData.filter(s => s.status === 'ON').length;
    counts.offline = awsData.filter(s => s.status !== 'ON').length;
  }

  useEffect(() => {
    if (!loading) {
      document.title = counts.grave > 0 ? `(${counts.grave}) ${TITULO_PADRAO}` : TITULO_PADRAO;
    }
    return () => { document.title = TITULO_PADRAO; };
  }, [loading, counts.grave]);

  const items = [
    {
      icon: <HiExclamationCircle size={16} />,
      value: counts.grave,
      label: 'Exceções',
      sublabel: 'Grave',
      nivel: 'Grave',
      status: loading ? 'neutral' : counts.grave > 0 ? 'danger' : 'success',
    },
    {
      icon: <HiBell size={16} />,
      value: counts.moderado,
      label: 'Alertas',
      sublabel: 'Moderado',
      nivel: 'Moderado',
      status: loading ? 'neutral' : counts.moderado > 0 ? 'warning' : 'success',
    },
    {
      icon: <HiInformationCircle size={16} />,
      value: counts.normal,
      label: 'Avisos',
      sublabel: 'Normal',
      nivel: 'Normal',
      status: loading ? 'neutral' : counts.normal > 0 ? 'success' : 'neutral',
    },
    {
      icon: <HiCheckCircle size={16} />,
      value: counts.online,
      label: 'Servidores Online',
      sublabel: null,
      nivel: null,
      status: loading ? 'neutral' : 'success',
    },
    {
      icon: <HiXCircle size={16} />,
      value: counts.offline,
      label: 'Servidores Offline',
      sublabel: null,
      nivel: null,
      status: loading ? 'neutral' : counts.offline > 0 ? 'danger' : 'neutral',
    },
  ];

  return (
    <div className="cs-kpi-row">
      {items.map((item, i) => {
        const isClickable = item.nivel != null;
        const isActive = filtroNivel === item.nivel;
        return (
          <KpiCard
            key={i}
            item={item}
            loading={loading}
            isClickable={isClickable}
            isActive={isActive}
            onClick={isClickable ? () => onFilterNivel(isActive ? null : item.nivel) : undefined}
          />
        );
      })}
      {lastUpdate && (
        <span className="cs-kpi-ts">
          atualizado {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  );
};

export default DashboardKPIs;
